import { ApolloError, NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { ErrorBoundary, ErrorRetry, SuspenseLoader, useAppUser } from '@bechtel/client-common';
import { GET_PROJECT_CONFIG_BY_ID_WITH_DISCIPLINES, SAVE_QTY_REPORTING_CONFIG } from '@bechtel/client-gql';
import {
  Box,
  ButtonAppearance,
  Separator,
  SeparatorType,
  Spinner,
  SpinnerSize,
  Text,
  ToastAction,
  ToastType,
  ToggleSwitch,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  ProjectConfig,
  SaveQuantityReportingConfigError,
  SaveQuantityReportingConfigInput,
  SaveQuantityReportingConfigPayload,
  WorkPackageDiscipline,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { Formik, FormikProps } from 'formik';
import { filter, get, map } from 'lodash';
import { Suspense, useEffect, useState } from 'react';
import { FooterButtonStyled, FooterStyled, ProjectAdminSubContainerStyled } from '../../styled';
import SaveQuantityReportingError from './error';
import { SaveQuantityReportingConfigErrorExt } from './error/types';
import { FormikState } from './types';
import { WorkWeekWindow } from './work-week-window';

let formikInitialValues: FormikState = {
  allowApprovalByNotification: false,
  workPackageDisciplineClaims: [],
  usersQuantityDisplayToggle: false,
  weekStartDay: 1,
  weekStartTime: '',
  weekEndDay: 0,
  weekEndTime: '',
};

export const General = () => {
  const { user } = useAppUser();
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const [qtyReportConfigError, setQtyReportConfigError] = useState<
    SaveQuantityReportingConfigError | SaveQuantityReportingConfigErrorExt | ApolloError | null
  >(null);
  const {
    loading: prjConfigLoading,
    error: prjConfigError,
    data: prjConfigData,
    refetch: prjConfigRefetch,
    networkStatus: prjConfigNetworkStatus,
  } = useQuery<{
    projectConfig: ProjectConfig;
  }>(GET_PROJECT_CONFIG_BY_ID_WITH_DISCIPLINES, {
    variables: { id: user?.project?.id },
    notifyOnNetworkStatusChange: true,
  });
  const wpDisciplineList: WorkPackageDiscipline[] = get(prjConfigData, 'projectConfigById.workPackageDisciplines', []);
  const activeDisciplines = filter(wpDisciplineList, 'isActive');
  const projectConfigData = get(prjConfigData, 'projectConfigById', undefined);

  const dispatchToast = useToastsDispatch();
  const QtyReportingConfigSaveToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Quantity reporting changes successfully updated.</Trans>
        </Text>
      </Box>
    );
  };

  const createSuccessToast: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      toastType: ToastType.INFO,
      content: QtyReportingConfigSaveToast,
      autoDismissTimeout: 5000,
    },
  };

  useEffect(() => {
    if (wpDisciplineList.length === 0 || !projectConfigData) return;
    const activeDisciplines = filter(wpDisciplineList, 'isActive');
    const workPackageDisciplineClaims = activeDisciplines.map(({ id, allowAnySequenceClaim }) => ({
      id,
      allowAnySequenceClaim,
    }));
    formikInitialValues = { ...formikInitialValues, ...projectConfigData, workPackageDisciplineClaims };
    setInitialValues(formikInitialValues);
  }, [wpDisciplineList, projectConfigData]);

  const [saveQtyReportingConfig, { loading: mutationLoading }] = useMutation<
    { saveQuantityReportingConfigPayload: SaveQuantityReportingConfigPayload },
    { input: SaveQuantityReportingConfigInput }
  >(SAVE_QTY_REPORTING_CONFIG, {
    onCompleted: (data) => {
      const qtyReportConfigError = get(data, 'saveQuantityReportingConfig.error');
      if (qtyReportConfigError) {
        setQtyReportConfigError(qtyReportConfigError);
      } else {
        setQtyReportConfigError(null);
        dispatchToast(createSuccessToast);
      }
    },
    onError: (error) => {
      setQtyReportConfigError(error);
    },
  });

  return (
    <ProjectAdminSubContainerStyled>
      {!prjConfigLoading && prjConfigError && (
        <div data-testid="prj-config-retry">
          <ErrorRetry refetch={prjConfigRefetch}>
            <Trans>
              There was a problem loading Quantity Reporting data. Please click the button below to try again.
            </Trans>
          </ErrorRetry>
        </div>
      )}
      {!prjConfigError && (
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={async (values: FormikState) => {
            const {
              weekStartDay,
              weekEndDay,
              weekEndTime,
              weekStartTime,
              allowApprovalByNotification,
              workPackageDisciplineClaims,
              usersQuantityDisplayToggle,
            } = values;
            const input: SaveQuantityReportingConfigInput = {
              allowApprovalByNotification,
              workPackageDisciplineClaims,
              usersQuantityDisplayToggle,
              weekStartDay,
              weekEndDay,
              weekEndTime,
              weekStartTime,
            };

            await saveQtyReportingConfig({
              variables: { input },
            });
          }}
        >
          {(formikProps: FormikProps<FormikState>) => {
            const { setFieldValue, values, dirty, handleSubmit, handleReset } = formikProps;
            const { allowApprovalByNotification, workPackageDisciplineClaims, usersQuantityDisplayToggle } = values;
            return (
              <Box data-testid="qty-config-container" flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
                <Box
                  style={{ overflowY: 'auto' }}
                  px={{ sm: '550' }}
                  pt={{ sm: '600' }}
                  flexDirection={{ sm: 'column' }}
                  flexGrow={{ sm: 1 }}
                >
                  <Box mb={{ sm: '600' }} justifyContent={{ sm: 'space-between' }}>
                    <Box>
                      <Text family="graphik" size="500" weight="600" color="grey.1000">
                        <Trans>Approving Claims</Trans>
                      </Text>
                    </Box>
                  </Box>
                  <Box mb={{ sm: '600' }}>
                    <Box data-testid="approve-claim-toggle" mr={{ sm: '500' }}>
                      <ToggleSwitch
                        disabled={true}
                        isToggled={allowApprovalByNotification}
                        onToggle={() => {
                          setFieldValue('allowApprovalByNotification', !allowApprovalByNotification);
                        }}
                      />
                    </Box>
                    <Text family="graphik" size="300" color="grey.1000">
                      <Trans>Allow users to approve claims directly through a notification</Trans>
                    </Text>
                  </Box>
                  <Separator type={SeparatorType.HORIZONTAL} spacing="000" color="grey.300" />
                  <Box mt={{ sm: '600' }} mb={{ sm: '450' }}>
                    <Text family="graphik" size="500" weight="600" color="grey.1000">
                      <Trans>Claiming Milestones</Trans>
                    </Text>
                  </Box>
                  <Box mb={{ sm: '600' }}>
                    <Text family="graphik" size="300" color="grey.1000">
                      <Trans>Allow users to claim milestones in any sequence?</Trans>
                    </Text>
                  </Box>
                  <Box mb={{ sm: '700' }}>
                    {!prjConfigLoading && prjConfigError && (
                      <div data-testid="wp-discipline-error-retry">
                        <ErrorRetry refetch={prjConfigRefetch}>
                          <Trans>
                            There was a problem loading Work Package Disciplines list. Please click the button below to
                            try again.
                          </Trans>
                        </ErrorRetry>
                      </div>
                    )}
                    {!prjConfigLoading && !prjConfigError && (
                      <Box data-testid="wp-discipline-data" flexDirection={{ sm: 'column' }}>
                        {map(activeDisciplines, (item, index) => {
                          return (
                            <Box data-testid={`wp-discipline-item-${index}`} mb={{ sm: '500' }} key={item.id}>
                              <Box mr={{ sm: '500' }}>
                                <ToggleSwitch
                                  isToggled={
                                    workPackageDisciplineClaims.length > 0 &&
                                    workPackageDisciplineClaims[index].allowAnySequenceClaim
                                  }
                                  onToggle={() => {
                                    setFieldValue(`workPackageDisciplineClaims[${index}]`, {
                                      ...workPackageDisciplineClaims[index],
                                      allowAnySequenceClaim: !workPackageDisciplineClaims[index].allowAnySequenceClaim,
                                    });
                                  }}
                                />
                              </Box>
                              <Text family="graphik" size="300" color="grey.1000">
                                {item.name}
                              </Text>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                  <Separator type={SeparatorType.HORIZONTAL} spacing="000" color="grey.300" />
                  <Box mb={{ sm: '500' }} mt={{ sm: '600' }}>
                    <Text family="graphik" size="500" weight="600" color="grey.1000">
                      <Trans>Quantities vs. Hours</Trans>
                    </Text>
                  </Box>
                  <Box mb={{ sm: '700' }}>
                    <Box data-testid="qty-display-toggle" mr={{ sm: '500' }}>
                      {/* TODO: Option to choose between Hours/Linear Feet will be visible on profile screen if this toggle is turned on */}
                      <ToggleSwitch
                        isToggled={usersQuantityDisplayToggle}
                        onToggle={() => {
                          setFieldValue('usersQuantityDisplayToggle', !usersQuantityDisplayToggle);
                        }}
                      />
                    </Box>
                    <Text family="graphik" size="300" color="grey.1000">
                      <Trans>
                        Allow users to choose whether their application interface displays hours or quantities
                      </Trans>
                    </Text>
                  </Box>
                  <Separator type={SeparatorType.HORIZONTAL} spacing="000" color="grey.300" />
                  <Box mb={{ sm: '500' }} mt={{ sm: '600' }}>
                    <Text family="graphik" size="500" weight="600" color="grey.1000">
                      <Trans>Work Week Window</Trans>
                    </Text>
                  </Box>
                  <Box mb={{ sm: '500' }}>
                    <Text family="graphik" size="300" color="grey.1000">
                      <Trans>Select the start and end times for quantity posting</Trans>
                    </Text>
                  </Box>
                  <WorkWeekWindow
                    formikProps={formikProps}
                    label={t`Start work week on:`}
                    dayFieldName="weekStartDay"
                    timeFieldName="weekStartTime"
                  />
                  <WorkWeekWindow
                    formikProps={formikProps}
                    label={t`End work week on:`}
                    dayFieldName="weekEndDay"
                    timeFieldName="weekEndTime"
                  />
                </Box>
                <FooterStyled>
                  {(prjConfigLoading || prjConfigNetworkStatus === NetworkStatus.refetch) && (
                    <Box
                      flexGrow={{ sm: 1 }}
                      alignItems={{ sm: 'center' }}
                      flexDirection={{ sm: 'column' }}
                      justifyContent={{ sm: 'center' }}
                      data-testid="prj-config-loading"
                    >
                      <Spinner size={SpinnerSize.SMALL} />
                    </Box>
                  )}
                  <Box>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled
                        data-testid="qty-general-cancel"
                        disabled={!dirty || prjConfigLoading || mutationLoading}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={() => handleReset()}
                      >
                        <Trans>Discard Changes</Trans>
                      </FooterButtonStyled>
                    </Box>
                    <FooterButtonStyled
                      data-testid="qty-general-save"
                      disabled={!dirty || prjConfigLoading || mutationLoading}
                      appearance={ButtonAppearance.SECONDARY}
                      onClick={() => handleSubmit()}
                    >
                      {mutationLoading && <Spinner />}
                      {!mutationLoading && t`Save Changes`}
                    </FooterButtonStyled>
                  </Box>
                </FooterStyled>
              </Box>
            );
          }}
        </Formik>
      )}
      {qtyReportConfigError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <SaveQuantityReportingError
              saveQtyReportingConfigError={qtyReportConfigError}
              close={() => {
                setQtyReportConfigError(null);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </ProjectAdminSubContainerStyled>
  );
};
